import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Music, Headphones, PartyPopper } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getSettings, getPackages, getReviews, getWidgets } from '../lib/api';

const HomePage = () => {
  const [settings, setSettings] = useState(null);
  const [packages, setPackages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [widgets, setWidgets] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, packagesRes, reviewsRes, widgetsRes] = await Promise.all([
          getSettings(),
          getPackages(),
          getReviews(),
          getWidgets()
        ]);
        setSettings(settingsRes.data);
        setPackages(packagesRes.data.slice(0, 3));
        setReviews(reviewsRes.data.slice(0, 3));
        
        // Convert widgets array to object by id
        const widgetMap = {};
        widgetsRes.data.forEach(w => {
          widgetMap[w.id] = w;
        });
        setWidgets(widgetMap);
      } catch (error) {
        console.error('Error fetching home data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] sm:min-h-screen flex items-center justify-center pt-16 sm:pt-20 overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1751922001359-21363cb804d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxkaiUyMHBlcmZvcm1pbmclMjBlbGVnYW50JTIwd2hpdGUlMjBwYXJ0eSUyMGNyb3dkfGVufDB8fHx8MTc3MzA3NjMzM3ww&ixlib=rb-4.1.0&q=85')` 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/70 to-white" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-16 sm:py-32 text-center">
          <p className="text-xs sm:text-sm font-semibold tracking-widest uppercase text-neutral-500 mb-4 sm:mb-6 animate-fade-in">
            Professional DJ Services
          </p>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold tracking-tight leading-tight text-neutral-900 mb-4 sm:mb-6 animate-fade-in animation-delay-100" style={{ fontFamily: 'Syne, sans-serif' }}>
            {settings?.hero_title || 'Your Event, Our Soundtrack'}
          </h1>
          <p className="text-base sm:text-lg md:text-xl leading-relaxed text-neutral-500 max-w-2xl mx-auto mb-8 sm:mb-10 animate-fade-in animation-delay-200 px-4">
            {settings?.hero_subtitle || 'Professional DJ services for weddings, parties, and corporate events'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center animate-fade-in animation-delay-300 px-4">
            <Link 
              to="/packages" 
              className="bg-neutral-900 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl shadow-lg shadow-neutral-900/20 text-sm sm:text-base"
              data-testid="hero-packages-btn"
            >
              View Packages
            </Link>
            <Link 
              to="/availability" 
              className="border-2 border-neutral-200 bg-white/80 backdrop-blur-sm px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition-all duration-300 hover:border-neutral-900 hover:bg-neutral-50 text-neutral-900 text-sm sm:text-base"
              data-testid="hero-availability-btn"
            >
              Check Availability
            </Link>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-16 sm:py-24 md:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-xs sm:text-sm font-semibold tracking-widest uppercase text-neutral-500 mb-3 sm:mb-4">What We Offer</p>
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight text-neutral-900" style={{ fontFamily: 'Syne, sans-serif' }}>Services</h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white border border-neutral-100 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-shadow duration-300 text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <Music className="w-6 h-6 sm:w-8 sm:h-8 text-neutral-900" />
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>Weddings</h3>
              <p className="text-sm sm:text-base leading-relaxed text-neutral-500">
                Make your special day unforgettable with custom playlists and professional MC services.
              </p>
            </div>
            
            <div className="bg-white border border-neutral-100 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-shadow duration-300 text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <Headphones className="w-6 h-6 sm:w-8 sm:h-8 text-neutral-900" />
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>Corporate Events</h3>
              <p className="text-sm sm:text-base leading-relaxed text-neutral-500">
                Professional entertainment for company parties, galas, and business gatherings.
              </p>
            </div>
            
            <div className="bg-white border border-neutral-100 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-shadow duration-300 text-center sm:col-span-2 lg:col-span-1">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <PartyPopper className="w-6 h-6 sm:w-8 sm:h-8 text-neutral-900" />
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>Private Parties</h3>
              <p className="text-sm sm:text-base leading-relaxed text-neutral-500">
                Birthday parties, anniversaries, and celebrations of all kinds with the perfect vibe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Preview */}
      {packages.length > 0 && (
        <section className="py-16 sm:py-24 md:py-32 bg-neutral-50" data-testid="packages-preview-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-8 sm:mb-12 gap-4">
              <div>
                <p className="text-xs sm:text-sm font-semibold tracking-widest uppercase text-neutral-500 mb-3 sm:mb-4">Our Offerings</p>
                <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight text-neutral-900" style={{ fontFamily: 'Syne, sans-serif' }}>Packages</h2>
              </div>
              <Link to="/packages" className="hidden sm:flex items-center gap-2 text-neutral-900 font-semibold hover:gap-4 transition-all text-sm sm:text-base">
                View All <ArrowRight size={20} />
              </Link>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {packages.map((pkg) => (
                <div 
                  key={pkg.id} 
                  className={`bg-white border border-neutral-100 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-shadow duration-300 relative ${pkg.is_popular ? 'border-2 border-neutral-900' : ''}`}
                  data-testid={`package-card-${pkg.id}`}
                >
                  {pkg.is_popular && (
                    <span className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2 bg-neutral-900 text-white px-3 sm:px-4 py-1 text-xs font-semibold tracking-wider uppercase">Most Popular</span>
                  )}
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>{pkg.name}</h3>
                  <p className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-3 sm:mb-4">{pkg.price}</p>
                  <p className="text-sm sm:text-base leading-relaxed text-neutral-500 mb-4 sm:mb-6">{pkg.description}</p>
                  <ul className="space-y-2">
                    {pkg.features.slice(0, 4).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-neutral-600 text-sm sm:text-base">
                        <span className="w-1.5 h-1.5 bg-neutral-900 rounded-full flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            <Link to="/packages" className="sm:hidden btn-outline block text-center mt-6 py-3 border-2 border-neutral-200 rounded-full font-semibold">
              View All Packages
            </Link>
          </div>
        </section>
      )}

      {/* Reviews */}
      {reviews.length > 0 && (
        <section className="py-16 sm:py-24 md:py-32 bg-white overflow-hidden" data-testid="reviews-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 mb-8 sm:mb-12">
            <p className="text-xs sm:text-sm font-semibold tracking-widest uppercase text-neutral-500 mb-3 sm:mb-4 text-center">What People Say</p>
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight text-neutral-900 text-center" style={{ fontFamily: 'Syne, sans-serif' }}>Reviews</h2>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {reviews.map((review) => (
                <div key={review.id} className="bg-white border border-neutral-100 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`review-card-${review.id}`}>
                  <div className="flex gap-1 mb-3 sm:mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={14}
                        className={i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-neutral-200'}
                      />
                    ))}
                  </div>
                  <p className="text-sm sm:text-base leading-relaxed text-neutral-500 mb-3 sm:mb-4">"{review.content}"</p>
                  <div>
                    <p className="font-semibold text-neutral-900 text-sm sm:text-base">{review.name}</p>
                    <p className="text-xs sm:text-sm text-neutral-500">{review.event_type}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="text-center mt-6 sm:mt-8">
            <Link to="/reviews" className="text-neutral-600 hover:text-neutral-900 font-medium text-sm sm:text-base">
              Read All Reviews →
            </Link>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 sm:py-24 md:py-32 bg-neutral-900 text-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 sm:mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Ready to Make Your Event Unforgettable?</h2>
          <p className="text-base sm:text-lg md:text-xl leading-relaxed text-neutral-400 max-w-2xl mx-auto mb-8 sm:mb-10 px-4">
            Let's discuss your event and create the perfect soundtrack together.
          </p>
          <Link to="/contact" className="bg-white text-neutral-900 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold hover:bg-neutral-100 transition-colors inline-block text-sm sm:text-base">
            Get Started
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
